// The book-colour band behind a caption (owner, 2026-10-06 — « like the book
// story of Roaa … a pink frame »). A text element with `bandFill` gets a band
// of that colour on its stored box, behind the words, at `bandOpacity` (88 %
// unless stated): the editor's text box, its read-only twin and the print
// raster all read it HERE, so the three cannot disagree.
// captionLayout.ts writes the fields. Guard: textBand.test.ts

export interface BandFields { bandFill?: unknown; bandOpacity?: unknown }
export interface Band { r: number; g: number; b: number; alpha: number; hex: string }

export const BAND_DEFAULT_OPACITY = 0.88;

/** The band of a text element, or null when it has none we can print. */
export function textBand(el: BandFields): Band | null {
  const hex = String(el.bandFill ?? '').trim().toUpperCase();
  if (!/^#[0-9A-F]{6}$/.test(hex)) return null;
  const raw = Number(el.bandOpacity ?? BAND_DEFAULT_OPACITY);
  const alpha = Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : BAND_DEFAULT_OPACITY;
  return { r: parseInt(hex.slice(1, 3), 16) / 255, g: parseInt(hex.slice(3, 5), 16) / 255, b: parseInt(hex.slice(5, 7), 16) / 255, alpha, hex };
}

/** The same band as a CSS background (the photo under it shows through the alpha). */
export function bandCss(el: BandFields): string | undefined {
  const b = textBand(el);
  if (!b) return undefined;
  return `rgba(${Math.round(b.r * 255)}, ${Math.round(b.g * 255)}, ${Math.round(b.b * 255)}, ${b.alpha})`;
}

/**
 * The band's rectangle: the stored box grown by `bandPad` (% of the page /
 * face WIDTH) on all four sides — the poem's dark box sits a little wider than
 * the words (COVER TEXT, 2026-10-30). Units are the caller's (points in the
 * print, % of the width in the editor); `y` grows the way the caller's does.
 */
export function bandRect(
  el: { bandPad?: unknown },
  box: { x: number; y: number; w: number; h: number },
  pageW: number,
): { x: number; y: number; w: number; h: number } {
  const raw = Number(el.bandPad ?? 0);
  const pad = Number.isFinite(raw) && raw > 0 ? (Math.min(50, raw) / 100) * pageW : 0;
  return { x: box.x - pad, y: box.y - pad, w: box.w + 2 * pad, h: box.h + 2 * pad };
}
